import { useEffect, useRef } from "react";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { PublicVariables } from "../../constants/PublicVariables";

/**
 * LDFlagsSync - Synchronizes LaunchDarkly flags to PublicVariables.ldFlags
 *
 * This component must be rendered INSIDE the LDProvider context.
 * It uses LD hooks to wait for initialization and then updates PublicVariables.ldFlags
 * with the actual flag values from LaunchDarkly.
 *
 * @param {Function} onFlagsReady - Callback function called when flags are synchronized
 */
const LDFlagsSync = ({ onFlagsReady }) => {
    const ldFlags = useFlags();
    const ldClient = useLDClient();
    const hasNotifiedRef = useRef(false); // Track if we've already notified parent

    // Primary sync: Wait for LD client to initialize and get all flags
    useEffect(() => {
        if (!ldClient) {
            console.log("LDFlagsSync: LD client not available");
            return;
        }

        const syncFlags = async () => {
            try {
                console.log("LDFlagsSync: Waiting for LD initialization...");
                await ldClient.waitForInitialization();
                PublicVariables.ldFlags = ldFlags;
                console.log("LDFlagsSync: Flags synchronized to PublicVariables:", PublicVariables.ldFlags);

                // Notify parent only once
                if (onFlagsReady && !hasNotifiedRef.current) {
                    hasNotifiedRef.current = true;
                    onFlagsReady(true);
                }
            } catch (error) {
                console.error("LDFlagsSync: Error synchronizing flags:", error);
            }
        };

        syncFlags();
        // Remove onFlagsReady from dependencies to prevent re-runs when callback reference changes
    }, [ldClient]); // eslint-disable-line react-hooks/exhaustive-deps

    // Secondary sync: Update when flags change (handles real-time flag updates)
    useEffect(() => {
        if (Object.keys(ldFlags).length > 0) {
            PublicVariables.ldFlags = ldFlags;
            console.log("LDFlagsSync: Flags updated from useFlags hook:", ldFlags);

            // Notify parent only once (in case primary sync didn't run)
            if (onFlagsReady && !hasNotifiedRef.current) {
                hasNotifiedRef.current = true;
                onFlagsReady(true);
            }
        }
        // Remove onFlagsReady from dependencies to prevent re-runs when callback reference changes
    }, [ldFlags]); // eslint-disable-line react-hooks/exhaustive-deps

    // This component doesn't render anything
    return null;
};

export default LDFlagsSync;
